import {
  VALID_ROLES,
  PERMISSIONS_MATRIX,
  can,
  canEditStudent,
  DISTRICT_DASHBOARD_ROLES
} from '@/config/roles'
import {
  ROLE_CAPABILITY_EXPECTATIONS,
  ADMIN_PANEL_ACTIONS,
  EXPECTED_DISTRICT_DASHBOARD_ROLES,
  expectedCanEdit,
  expectedCanSendFeedback
} from '@/config/rolePermissionExpectations'
import { RoleUtils } from '@/composables/roles/roleConfig'

// Read-only, covers ALL roles at once: asserts the app's live permission code
// (PERMISSIONS_MATRIX / canEditStudent / canSendFeedback — the layer that
// gates admin panels, student editing, and feedback UI) agrees with the
// hand-maintained expectations in config/rolePermissionExpectations.js.
// This is client-side contract checking; the live server-side answer for the
// CURRENT role is covered by the permission-probe and lifecycle scenarios.

const CHECK_UID = 'smoke-contract-uid'

const EDIT_CASES = [
  { name: 'own IEP', isOwn: true, plan: 'IEP' },
  { name: 'own 504', isOwn: true, plan: '504' },
  { name: 'own no-plan', isOwn: true, plan: null },
  { name: 'other IEP', isOwn: false, plan: 'IEP' },
  { name: 'other 504', isOwn: false, plan: '504' },
  { name: 'other no-plan', isOwn: false, plan: null }
]

const FEEDBACK_CASES = [
  { name: 'own·IEP', isOwn: true, teaches: false, plan: 'IEP' },
  { name: 'own·504', isOwn: true, teaches: false, plan: '504' },
  { name: 'other·IEP', isOwn: false, teaches: false, plan: 'IEP' },
  { name: 'other·504', isOwn: false, teaches: false, plan: '504' },
  { name: 'other·teaching', isOwn: false, teaches: true, plan: null },
  { name: 'other·no-plan', isOwn: false, teaches: false, plan: null }
]

const makeStudent = ({ isOwn, teaches, plan }) => {
  const caseManagerId = isOwn ? CHECK_UID : 'other-cm'
  const periods = teaches ? { 1: CHECK_UID } : { 1: 'other-teacher' }
  return {
    id: 'contract-student',
    caseManagerId,
    plan,
    app: { studentData: { caseManagerId, plan }, schedule: { periods } }
  }
}

const studentDataShim = {
  getCaseManagerId: (s) => s.app?.studentData?.caseManagerId || s.caseManagerId || null,
  getSchedule: (s) => s.app?.schedule?.periods || null
}

export default {
  id: 'roleContractMatrix',
  label: 'Role contract matrix (all roles)',
  description:
    'Admin-panel actions, student-edit scope, and feedback-send checked for every role against the expectations table.',
  destructive: false,

  async run(ctx, step) {
    await step('every role is wired into the permission matrix', async () => {
      const missing = VALID_ROLES.filter(
        (r) => !PERMISSIONS_MATRIX[r] || !ROLE_CAPABILITY_EXPECTATIONS[r]
      )
      if (missing.length) throw new Error(`Roles missing config: ${missing.join(', ')}`)
      return `${VALID_ROLES.length} roles`
    })

    await step('admin panel access per role matches expectations', async () => {
      const mismatches = []
      for (const role of VALID_ROLES) {
        const exp = ROLE_CAPABILITY_EXPECTATIONS[role]
        if (!exp) continue
        for (const action of ADMIN_PANEL_ACTIONS) {
          const shouldHave = exp.adminActions.includes(action)
          if (can(role, action) !== shouldHave) {
            mismatches.push(`${role}/${action} (expected ${shouldHave})`)
          }
        }
      }
      if (mismatches.length) {
        throw new Error(`${mismatches.length} mismatch(es): ${mismatches.slice(0, 6).join('; ')}`)
      }
      return `${VALID_ROLES.length} roles × ${ADMIN_PANEL_ACTIONS.length} actions OK`
    })

    await step('district dashboard access per role matches expectations', async () => {
      // District Dashboard (/district route + nav item) is district-tier only —
      // school admin / school_admin must NOT have it.
      const mismatches = []
      for (const role of VALID_ROLES) {
        const shouldHave = EXPECTED_DISTRICT_DASHBOARD_ROLES.includes(role)
        const actual = DISTRICT_DASHBOARD_ROLES.includes(role)
        if (actual !== shouldHave) {
          mismatches.push(`${role} (expected ${shouldHave}, got ${actual})`)
        }
      }
      // Guard against a role sneaking into DISTRICT_DASHBOARD_ROLES that isn't
      // even a valid role.
      const stray = DISTRICT_DASHBOARD_ROLES.filter((r) => !VALID_ROLES.includes(r))
      if (stray.length) mismatches.push(`unknown role(s): ${stray.join(', ')}`)
      if (mismatches.length) {
        throw new Error(`${mismatches.length} mismatch(es): ${mismatches.join('; ')}`)
      }
      return `${VALID_ROLES.length} roles OK (district: ${EXPECTED_DISTRICT_DASHBOARD_ROLES.join(', ')})`
    })

    await step('student-edit scope per role matches expectations', async () => {
      const mismatches = []
      for (const role of VALID_ROLES) {
        for (const c of EDIT_CASES) {
          const expected = expectedCanEdit(role, c)
          const actual = canEditStudent(makeStudent(c), { uid: CHECK_UID, role })
          if (expected !== actual) {
            mismatches.push(`${role} · ${c.name} (expected ${expected}, got ${actual})`)
          }
        }
      }
      if (mismatches.length) {
        throw new Error(`${mismatches.length} mismatch(es): ${mismatches.slice(0, 6).join('; ')}`)
      }
      return `${VALID_ROLES.length} roles × ${EDIT_CASES.length} cases OK`
    })

    await step('feedback-send per role matches expectations', async () => {
      const mismatches = []
      for (const role of VALID_ROLES) {
        for (const c of FEEDBACK_CASES) {
          const expected = expectedCanSendFeedback(role, c)
          const actual = !!RoleUtils.canSendFeedback(
            CHECK_UID,
            role,
            makeStudent(c),
            studentDataShim
          )
          if (expected !== actual) {
            mismatches.push(`${role} · ${c.name} (expected ${expected}, got ${actual})`)
          }
        }
      }
      if (mismatches.length) {
        throw new Error(`${mismatches.length} mismatch(es): ${mismatches.slice(0, 6).join('; ')}`)
      }
      return `${VALID_ROLES.length} roles × ${FEEDBACK_CASES.length} cases OK`
    })
  }
}
